import { useCallback, useRef, useState } from 'react'

import { CHAMADA_PAINEL_TIMERS } from '../constants-chamadaPainel'
import { Chamada } from '../model-chamadaPainel'

export interface UseFilaChamadasResult {
  chamadaAtual: Chamada | null
  chamadasProcessadas: Chamada[]
  isChamando: boolean
  setEstadoInicial: (chamadas: Chamada[]) => void
  enfileirar: (chamadas: Chamada[]) => void
}

export function useFilaChamadas(maxChamadasProcessadas: number): UseFilaChamadasResult {
  const [chamadaAtual, setChamadaAtual] = useState<Chamada | null>(null)
  const [chamadasProcessadas, setChamadasProcessadas] = useState<Chamada[]>([])
  const [isChamando, setIsChamando] = useState(false)

  const filaRef = useRef<Chamada[]>([])
  const seenIdsRef = useRef<Set<number>>(new Set())
  const processingRef = useRef(false)

  const processarProxima = useCallback(() => {
    const proxima = filaRef.current.shift()

    if (proxima) {
      setChamadaAtual(proxima)
      setChamadasProcessadas((prev) => [proxima, ...prev].slice(0, maxChamadasProcessadas))
      setIsChamando(true)
      setTimeout(processarProxima, CHAMADA_PAINEL_TIMERS.DESTAQUE_CHAMADA)
    } else {
      setIsChamando(false)
      processingRef.current = false
    }
  }, [maxChamadasProcessadas])

  const setEstadoInicial = useCallback(
    (chamadas: Chamada[]) => {
      chamadas.forEach((c) => seenIdsRef.current.add(c.id))
      setChamadaAtual(chamadas[0] ?? null)
      setChamadasProcessadas(chamadas.slice(0, maxChamadasProcessadas))
    },
    [maxChamadasProcessadas]
  )

  const enfileirar = useCallback(
    (chamadas: Chamada[]) => {
      const chamadasInvertidas = [...chamadas].reverse()
      const novasChamadas = chamadasInvertidas.filter((c) => !seenIdsRef.current.has(c.id))
      novasChamadas.forEach((c) => seenIdsRef.current.add(c.id))
      filaRef.current.push(...novasChamadas)

      if (!processingRef.current && filaRef.current.isNotEmpty()) {
        processingRef.current = true
        processarProxima()
      }
    },
    [processarProxima]
  )

  return { chamadaAtual, chamadasProcessadas, isChamando, setEstadoInicial, enfileirar }
}
